import { 
  collection, 
  query, 
  where, 
  getDocs,
  getDoc,
  updateDoc, 
  doc, 
  addDoc,
  deleteDoc,
  orderBy 
} from 'firebase/firestore';
import { 
  timeToMinutes, 
  minutesToTime, 
  checkTimeOverlap, 
  RESERVATION_DURATION 
} from './tableService';
import { db } from './config';
  
export const adminService = {
  // Obtener reservas por fecha
  getReservationsByDate: async (date) => {
    try {
      const reservationsRef = collection(db, 'reservations');
      // Convertir la fecha al formato correcto (dd/mm/yyyy)
      const formattedDate = new Date(date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
  
      const q = query(
        reservationsRef,
        where('date', '==', formattedDate)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting reservations:', error);
      throw error;
    }
  },

  // Verificar disponibilidad de una mesa específica en un momento dado
  checkTableAvailability: async (tableId, date, time, reservationIdToExclude = null) => {
    try {
      // Convertir la fecha al formato correcto (dd/mm/yyyy)
      const formattedDate = typeof date === 'string' && date.includes('/') 
        ? date 
        : new Date(date).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });

      // Obtener reservas existentes para ese día
      const reservationsRef = collection(db, 'reservations');
      const q = query(
        reservationsRef,
        where('date', '==', formattedDate),
        where('status', 'in', ['RESERVA CONFIRMADA', 'PENDIENTE DE CONFIRMACION', 'SENTADA', 'LLEGADA'])
      );
      
      const snapshot = await getDocs(q);
      const existingReservations = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        // Excluir la reserva actual si estamos actualizando
        .filter(res => res.id !== reservationIdToExclude);

      // Convertir el tiempo solicitado a minutos
      const requestedTimeInMinutes = timeToMinutes(time);
      
      // Calcular el tiempo de finalización (1.5 horas después)
      const requestedEndTimeInMinutes = requestedTimeInMinutes + RESERVATION_DURATION;
      const requestedEndTime = minutesToTime(requestedEndTimeInMinutes);
      
      // Calcular el tiempo de inicio con buffer (1.5 horas antes)
      const requestedStartTimeWithBuffer = minutesToTime(Math.max(0, requestedTimeInMinutes - RESERVATION_DURATION));

      // Verificar si la mesa está disponible (no hay conflictos de horario)
      const tableIsOccupied = existingReservations.some(reservation => {
        // Verificar si esta reserva tiene esta mesa
        if (reservation.tables && reservation.tables.includes(tableId)) {
          // Convertir el tiempo de la reserva existente a minutos
          const reservationTimeInMinutes = timeToMinutes(reservation.time);
          
          // Calcular el tiempo de finalización de la reserva existente
          const reservationEndTimeInMinutes = reservationTimeInMinutes + RESERVATION_DURATION;
          const reservationEndTime = minutesToTime(reservationEndTimeInMinutes);
          
          // Calcular el tiempo de inicio con buffer de la reserva existente
          const reservationStartTimeWithBuffer = minutesToTime(Math.max(0, reservationTimeInMinutes - RESERVATION_DURATION));
          
          // Verificar si hay solapamiento entre:
          // El tiempo solicitado (con buffer) y el tiempo de la reserva existente (con buffer)
          return checkTimeOverlap(
            requestedStartTimeWithBuffer, requestedEndTime,
            reservationStartTimeWithBuffer, reservationEndTime
          );
        }
        return false;
      });

      // La mesa está disponible si NO está ocupada
      return !tableIsOccupied;
    } catch (error) {
      console.error('Error verificando disponibilidad de mesa:', error);
      return false; // Si hay error, asumir que no está disponible por seguridad
    }
  },

  // Función para encontrar mesas disponibles alternativas
  findAvailableTables: async (date, time, guests, excludeTables = []) => {
    try {
      // Obtener todas las mesas
      const tablesRef = collection(db, 'tables');
      const snapshot = await getDocs(tablesRef);
      
      // Verificar disponibilidad de cada mesa
      const availabilityPromises = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(table => !excludeTables.includes(table.id)) // Excluir mesas ya verificadas
        .map(async table => {
          const isAvailable = await adminService.checkTableAvailability(table.id, date, time);
          return {
            ...table,
            available: isAvailable
          };
        });
      
      const tablesWithAvailability = await Promise.all(availabilityPromises);
      
      // Filtrar solo las mesas disponibles y ordenar por capacidad
      return tablesWithAvailability
        .filter(table => table.available)
        .sort((a, b) => (b.capacity || 3) - (a.capacity || 3)); // Ordenar de mayor a menor capacidad
    } catch (error) {
      console.error('Error buscando mesas disponibles:', error);
      return [];
    }
  },

  // Función para asignar automáticamente mesas según el número de comensales
  autoAssignTables: async (date, time, guests, excludeTables = []) => {
    try {
      // Obtener todas las mesas disponibles
      const availableTables = await adminService.findAvailableTables(date, time, guests, excludeTables);
      
      // Si no hay mesas disponibles, retornar un array vacío
      if (!availableTables || availableTables.length === 0) {
        return [];
      }
      
      let remainingGuests = guests;
      const assignedTables = [];
      
      // Primero intentamos asignar mesas exactas (capacidad = número de comensales)
      const exactCapacityTable = availableTables.find(table => (table.capacity || 3) === remainingGuests);
      if (exactCapacityTable) {
        return [exactCapacityTable.id];
      }
      
      // Luego intentamos asignar la mesa más grande que pueda acomodar a todos
      const largestTable = availableTables.find(table => (table.capacity || 3) >= remainingGuests);
      if (largestTable) {
        return [largestTable.id];
      }
      
      // Si no, empezamos a combinar mesas comenzando por las más grandes
      for (const table of availableTables) {
        if (remainingGuests <= 0) break;
        
        assignedTables.push(table.id);
        remainingGuests -= (table.capacity || 3);
      }
      
      // Si todavía quedan comensales sin asignar, no tenemos suficientes mesas
      if (remainingGuests > 0) {
        return []; // No hay suficientes mesas disponibles
      }
      
      return assignedTables;
    } catch (error) {
      console.error('Error asignando mesas automáticamente:', error);
      return [];
    }
  },

  createReservation: async (reservationData) => {
    try {
      // Convertir la fecha al formato correcto
      const formattedDate = new Date(reservationData.date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
  
      // Asegurarnos de que tenemos un array de mesas válido
      let tables = reservationData.tables || reservationData.selectedTables || [];
  
      // Verificar la disponibilidad real de las mesas seleccionadas
      if (tables.length > 0) {
        const tableAvailabilityPromises = tables.map(tableId => 
          adminService.checkTableAvailability(tableId, formattedDate, reservationData.time)
        );
        
        const tableAvailability = await Promise.all(tableAvailabilityPromises);
        const allTablesAvailable = tableAvailability.every(isAvailable => isAvailable);
        
        if (!allTablesAvailable) {
          console.log(`Algunas mesas seleccionadas no están disponibles, buscando alternativas...`);
          
          // Identificar mesas disponibles entre las seleccionadas
          const availableTables = tables.filter((tableId, index) => tableAvailability[index]);
          
          // Si tenemos al menos una mesa disponible, intentamos completar con otras
          if (availableTables.length > 0) {
            // Asignar automáticamente mesas adicionales si es necesario
            const additionalTables = await adminService.autoAssignTables(
              formattedDate, 
              reservationData.time, 
              reservationData.guests,
              availableTables // Excluir mesas que ya sabemos que están disponibles
            );
            
            if (additionalTables.length > 0) {
              // Combinar las mesas disponibles con las adicionales
              tables = [...availableTables, ...additionalTables];
              console.log(`Reasignación exitosa: ${tables.join(', ')}`);
            } else {
              // Si no pudimos asignar mesas adicionales, solo usamos las disponibles
              tables = availableTables;
              console.log(`Asignación parcial: ${tables.join(', ')}`);
            }
          } else {
            // Ninguna mesa disponible, intentamos asignar todas nuevamente
            const alternativeTables = await adminService.autoAssignTables(
              formattedDate, 
              reservationData.time, 
              reservationData.guests
            );
            
            if (alternativeTables.length > 0) {
              tables = alternativeTables;
              console.log(`Asignación alternativa: ${tables.join(', ')}`);
            } else {
              // No hay suficientes mesas disponibles
              throw new Error(`No hay suficientes mesas disponibles para esta reserva.`);
            }
          }
        }
      } else if (reservationData.guests > 0) {
        // Si no hay mesas seleccionadas pero hay comensales, intentamos asignar automáticamente
        const autoAssignedTables = await adminService.autoAssignTables(
          formattedDate, 
          reservationData.time, 
          reservationData.guests
        );
        
        if (autoAssignedTables.length > 0) {
          tables = autoAssignedTables;
          console.log(`Asignación automática: ${tables.join(', ')}`);
        } else {
          console.log(`No se pudieron asignar mesas automáticamente. Creando reserva sin mesas.`);
        }
      }
  
      // Preparar los datos de la reserva
      const reservationToCreate = {
        ...reservationData,
        date: formattedDate,
        createdAt: new Date().toISOString(),
        source: reservationData.source || 'admin',
        codigo_promo: reservationData.codigo_promo || null, // NUEVO CAMPO
        tables: tables
      };
  
      // Eliminar selectedTables si existe para evitar duplicación
      if ('selectedTables' in reservationToCreate) {
        delete reservationToCreate.selectedTables;
      }
  
      console.log('Datos a guardar:', reservationToCreate); // Para debugging
  
      const docRef = await addDoc(collection(db, 'reservations'), reservationToCreate);
  
      // Actualizar el estado de las mesas seleccionadas
      if (tables.length > 0) {
        const reservedUntil = new Date();
        // Convertir la fecha de string a objeto Date
        if (formattedDate.includes('/')) {
          const [day, month, year] = formattedDate.split('/');
          const [hours, minutes] = reservationData.time.split(':');
          reservedUntil.setFullYear(parseInt(year), parseInt(month) - 1, parseInt(day));
          reservedUntil.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        }
        
        // Marcar como reservada por 1.5 horas
        reservedUntil.setMinutes(reservedUntil.getMinutes() + RESERVATION_DURATION);
  
        await Promise.all(
          tables.map(tableId => 
            updateDoc(doc(db, 'tables', tableId), {
              reservedUntil: reservedUntil.toISOString()
            })
          )
        );
      }
  
      return {
        id: docRef.id,
        ...reservationToCreate
      };
    } catch (error) {
      console.error('Error creating reservation:', error);
      console.error('Datos que causaron el error:', reservationData); // Para debugging
      throw error;
    }
  },

  updateReservation: async (reservationId, updatedData) => {
    try {
      const reservationRef = doc(db, 'reservations', reservationId);
      
      // Obtener la reserva actual para comparar los cambios en las mesas
      const currentReservation = await getDoc(reservationRef);
      const currentData = currentReservation.data();
      
      // Formatear la fecha si ha cambiado
      const formattedDate = new Date(updatedData.date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
  
      // Verificar la disponibilidad de las mesas seleccionadas
      let tables = updatedData.selectedTables || [];
      const currentTables = currentData.tables || [];

      if ('codigo_promo' in updatedData) {
        await updateDoc(reservationRef, {
          ...updatedData,
          codigo_promo: updatedData.codigo_promo
        });
      }
      // Si las mesas han cambiado o la hora/fecha ha cambiado, verificar disponibilidad
      if (
        tables.join(',') !== currentTables.join(',') || 
        formattedDate !== currentData.date ||
        updatedData.time !== currentData.time
      ) {
        const tableAvailabilityPromises = tables.map(tableId => 
          adminService.checkTableAvailability(
            tableId, 
            formattedDate, 
            updatedData.time,
            reservationId // Excluir esta reserva de la verificación
          )
        );
        
        const tableAvailability = await Promise.all(tableAvailabilityPromises);
        const allTablesAvailable = tableAvailability.every(isAvailable => isAvailable);
        
        if (!allTablesAvailable) {
          console.log(`Algunas mesas seleccionadas no están disponibles para actualizar, buscando alternativas...`);
          
          // Identificar mesas disponibles entre las seleccionadas
          const availableTables = tables.filter((tableId, index) => tableAvailability[index]);
          
          // Si tenemos al menos una mesa disponible, intentamos completar con otras
          if (availableTables.length > 0) {
            // Asignar automáticamente mesas adicionales si es necesario
            const additionalTables = await adminService.autoAssignTables(
              formattedDate, 
              updatedData.time, 
              updatedData.guests,
              availableTables // Excluir mesas que ya sabemos que están disponibles
            );
            
            if (additionalTables.length > 0) {
              // Combinar las mesas disponibles con las adicionales
              tables = [...availableTables, ...additionalTables];
              console.log(`Reasignación exitosa para actualización: ${tables.join(', ')}`);
            } else {
              // Si no pudimos asignar mesas adicionales, solo usamos las disponibles
              tables = availableTables;
              console.log(`Asignación parcial para actualización: ${tables.join(', ')}`);
              
              // Verificar si las mesas disponibles son suficientes
              if (availableTables.length === 0) {
                throw new Error(`No hay mesas disponibles para esta reserva.`);
              }
            }
          } else {
            // Ninguna mesa disponible, intentamos asignar todas nuevamente
            const alternativeTables = await adminService.autoAssignTables(
              formattedDate, 
              updatedData.time, 
              updatedData.guests
            );
            
            if (alternativeTables.length > 0) {
              tables = alternativeTables;
              console.log(`Asignación alternativa para actualización: ${tables.join(', ')}`);
            } else {
              // No hay suficientes mesas disponibles
              throw new Error(`No hay suficientes mesas disponibles para esta reserva.`);
            }
          }
        }
      }
  
      // Preparar los datos actualizados
      const reservationToUpdate = {
        ...updatedData,
        date: formattedDate,
        updatedAt: new Date().toISOString(),
        // Mantener el origen original si existe, o asignar 'admin' si no lo tiene
        source: currentData.source || 'admin',
        tables: tables // Actualizar con las mesas verificadas
      };
      
      // Eliminar selectedTables si existe para evitar duplicación
      if ('selectedTables' in reservationToUpdate) {
        delete reservationToUpdate.selectedTables;
      }
  
      // Actualizar la reserva
      await updateDoc(reservationRef, reservationToUpdate);
  
      // Si las mesas han cambiado, actualizar las mesas
      if (currentTables.join(',') !== tables.join(',')) {
        // Liberar las mesas antiguas
        if (currentTables.length > 0) {
          await Promise.all(
            currentTables.map(tableId =>
              updateDoc(doc(db, 'tables', tableId), {
                reservedUntil: null
              })
            )
          );
        }
  
        // Reservar las nuevas mesas
        if (tables.length > 0) {
          const reservedUntil = new Date();
          // Convertir la fecha de string a objeto Date
          if (formattedDate.includes('/')) {
            const [day, month, year] = formattedDate.split('/');
            const [hours, minutes] = updatedData.time.split(':');
            reservedUntil.setFullYear(parseInt(year), parseInt(month) - 1, parseInt(day));
            reservedUntil.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          }
          
          // Marcar como reservada por 1.5 horas
          reservedUntil.setMinutes(reservedUntil.getMinutes() + RESERVATION_DURATION);
  
          await Promise.all(
            tables.map(tableId =>
              updateDoc(doc(db, 'tables', tableId), {
                reservedUntil: reservedUntil.toISOString()
              })
            )
          );
        }
      }
  
      return {
        id: reservationId,
        ...reservationToUpdate
      };
    } catch (error) {
      console.error('Error updating reservation:', error);
      throw error;
    }
  },

  // Obtener todas las reservas (histórico)
  getAllReservations: async () => {
    try {
      const reservationsRef = collection(db, 'reservations');
      const snapshot = await getDocs(reservationsRef);
      
      const reservations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Ordenar por fecha y hora
      return reservations.sort((a, b) => {
        const dateA = new Date(`${a.date} ${a.time}`);
        const dateB = new Date(`${b.date} ${b.time}`);
        return dateB - dateA; // Orden descendente (más reciente primero)
      });
    } catch (error) {
      console.error('Error getting all reservations:', error);
      throw error;
    }
  },

  // Modifica el método getDashboardStats para incluir el conteo de comensales
  getDashboardStats: async () => {
    try {
      const today = new Date().toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
  
      const reservationsRef = collection(db, 'reservations');
      const todayQuery = query(
        reservationsRef,
        where('date', '==', today)
      );
      
      const pendingQuery = query(
        reservationsRef,
        where('status', '==', 'PENDIENTE DE CONFIRMACION')
      );
  
      const [todaySnapshot, pendingSnapshot, allReservationsSnapshot] = await Promise.all([
        getDocs(todayQuery),
        getDocs(pendingQuery),
        getDocs(reservationsRef)
      ]);
  
      const todayReservations = todaySnapshot.docs.map(doc => doc.data());
      const allReservations = allReservationsSnapshot.docs.map(doc => doc.data());
      
      const activeGuests = todayReservations
        .filter(r => r.status === 'RESERVA CONFIRMADA' || r.status === 'SENTADA')
        .reduce((sum, r) => sum + (r.guests || 0), 0);
  
      // Agregar estadísticas por origen
      const sourceStats = {
        web: allReservations.filter(r => r.source === 'web').length,
        google: allReservations.filter(r => r.source === 'google').length,
        walkIn: allReservations.filter(r => r.source === 'walk-in').length,
        admin: allReservations.filter(r => r.source === 'admin').length,
        unknown: allReservations.filter(r => !r.source).length
      };
  
      // Calcular porcentajes
      const totalReservations = allReservationsSnapshot.docs.length;
      const sourcePercentages = {
        web: Math.round((sourceStats.web / totalReservations) * 100) || 0,
        google: Math.round((sourceStats.google / totalReservations) * 100) || 0,
        walkIn: Math.round((sourceStats.walkIn / totalReservations) * 100) || 0,
        admin: Math.round((sourceStats.admin / totalReservations) * 100) || 0,
        unknown: Math.round((sourceStats.unknown / totalReservations) * 100) || 0
      };
  
      return {
        totalReservations,
        todayReservations: todaySnapshot.docs.length,
        activeGuests,
        pendingReservations: pendingSnapshot.docs.length,
        bySource: sourceStats,
        bySourcePercentage: sourcePercentages
      };
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      throw error;
    }
  },

  // Actualizar estado de reserva
  updateReservationStatus: async (reservationId, status) => {
    try {
      const reservationRef = doc(db, 'reservations', reservationId);
      
      // Primero obtenemos la reserva actual
      const reservationDoc = await getDoc(reservationRef);
      const reservationData = reservationDoc.data();
      
      // Preparamos los datos a actualizar
      const updateData = {
        status,
        updatedAt: new Date().toISOString()
      };
      
      // Acciones específicas según el estado
      switch (status) {
        case 'RESERVA CONFIRMADA':
          // Al confirmar una reserva, verificar y reasignar mesas si es necesario
          if (Array.isArray(reservationData.tables) && reservationData.tables.length > 0) {
            const tablesNeeded = Math.ceil(reservationData.guests / 3);
            const currentTables = [...reservationData.tables];
            
            // Verificar disponibilidad de las mesas actuales
            const tableAvailabilityPromises = currentTables.map(tableId => 
              adminService.checkTableAvailability(tableId, reservationData.date, reservationData.time, reservationId)
            );
            
            const tableAvailability = await Promise.all(tableAvailabilityPromises);
            const allTablesAvailable = tableAvailability.every(isAvailable => isAvailable);
            
            if (!allTablesAvailable) {
              console.log(`[Reserva ${reservationId}] Algunas mesas no están disponibles, buscando alternativas...`);
              
              // Identificar mesas disponibles entre las asignadas
              const availableTables = currentTables.filter((tableId, index) => tableAvailability[index]);
              
              // Si tenemos mesas disponibles, intentamos completar con otras
              if (availableTables.length > 0) {
                // Asignar mesas adicionales automáticamente
                const alternativeTables = await adminService.autoAssignTables(
                  reservationData.date, 
                  reservationData.time, 
                  reservationData.guests,
                  availableTables // Excluir mesas que ya sabemos que están disponibles
                );
                
                if (alternativeTables.length > 0) {
                  // Combinar las mesas disponibles con las alternativas
                  const newTables = [...availableTables, ...alternativeTables];
                  
                  updateData.tables = newTables;
                  console.log(`[Reserva ${reservationId}] Reasignación exitosa: ${newTables.join(', ')}`);
                  
                  // Actualizar las nuevas mesas
                  const dateParts = reservationData.date.split('/');
                  const reservedUntil = new Date();
                  reservedUntil.setFullYear(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
                  const [hours, minutes] = reservationData.time.split(':');
                  reservedUntil.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                  reservedUntil.setMinutes(reservedUntil.getMinutes() + RESERVATION_DURATION);
                  
                  await Promise.all(
                    newTables.map(tableId => 
                      updateDoc(doc(db, 'tables', tableId), {
                        reservedUntil: reservedUntil.toISOString()
                      })
                    )
                  );
                } else {
                  // No pudimos asignar mesas adicionales, usamos las disponibles
                  updateData.tables = availableTables;
                  console.log(`[Reserva ${reservationId}] Asignación parcial: ${availableTables.join(', ')}`);
                  
                  // Actualizar las mesas disponibles
                  const dateParts = reservationData.date.split('/');
                  const reservedUntil = new Date();
                  reservedUntil.setFullYear(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
                  const [hours, minutes] = reservationData.time.split(':');
                  reservedUntil.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                  reservedUntil.setMinutes(reservedUntil.getMinutes() + RESERVATION_DURATION);
                  
                  await Promise.all(
                    availableTables.map(tableId => 
                      updateDoc(doc(db, 'tables', tableId), {
                        reservedUntil: reservedUntil.toISOString()
                      })
                    )
                  );
                }
              } else {
                // Ninguna mesa disponible, intentamos asignar todas nuevamente
                const allNewTables = await adminService.autoAssignTables(
                  reservationData.date, 
                  reservationData.time, 
                  reservationData.guests
                );
                
                if (allNewTables.length > 0) {
                  // Tenemos nuevas mesas para asignar
                  updateData.tables = allNewTables;
                  console.log(`[Reserva ${reservationId}] Asignación completa nueva: ${allNewTables.join(', ')}`);
                  
                  // Actualizar las nuevas mesas
                  const dateParts = reservationData.date.split('/');
                  const reservedUntil = new Date();
                  reservedUntil.setFullYear(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
                  const [hours, minutes] = reservationData.time.split(':');
                  reservedUntil.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                  reservedUntil.setMinutes(reservedUntil.getMinutes() + RESERVATION_DURATION);
                  
                  await Promise.all(
                    allNewTables.map(tableId => 
                      updateDoc(doc(db, 'tables', tableId), {
                        reservedUntil: reservedUntil.toISOString()
                      })
                    )
                  );
                } else {
                  // No hay suficientes mesas disponibles
                  throw new Error(`No hay suficientes mesas disponibles para esta reserva`);
                }
              }
            }
          } else if (reservationData.guests > 0) {
            // No hay mesas asignadas pero hay comensales, intentamos asignar automáticamente
            const newTables = await adminService.autoAssignTables(
              reservationData.date,
              reservationData.time,
              reservationData.guests
            );
            
            if (newTables.length > 0) {
              updateData.tables = newTables;
              console.log(`[Reserva ${reservationId}] Asignación automática: ${newTables.join(', ')}`);
              
              // Actualizar las nuevas mesas
              const dateParts = reservationData.date.split('/');
              const reservedUntil = new Date();
              reservedUntil.setFullYear(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
              const [hours, minutes] = reservationData.time.split(':');
              reservedUntil.setHours(parseInt(hours), parseInt(minutes), 0, 0);
              reservedUntil.setMinutes(reservedUntil.getMinutes() + RESERVATION_DURATION);
              
              await Promise.all(
                newTables.map(tableId => 
                  updateDoc(doc(db, 'tables', tableId), {
                    reservedUntil: reservedUntil.toISOString()
                  })
                )
              );
            } else {
              console.log(`[Reserva ${reservationId}] No se pudieron asignar mesas automáticamente.`);
            }
          }
          break;
            
        case 'PENDIENTE DE CONFIRMACION':
          // Estado inicial o volver a pendiente
          break;
            
        case 'LLEGADA':
          // Registrar llegada parcial de clientes
          updateData.arrivedAt = new Date().toISOString(); 
          break;
            
        case 'SENTADA':
          // Al sentar a los clientes, registramos la hora real de inicio
          const currentTime = new Date();
          updateData.startedAt = currentTime.toISOString();
          updateData.realTime = `${String(currentTime.getHours()).padStart(2, '0')}:${String(currentTime.getMinutes()).padStart(2, '0')}`;
          break;
            
        case 'LIBERADA':
          // Registrar finalización y liberar mesas
          updateData.endedAt = new Date().toISOString();
          
          // Liberar mesas
          if (Array.isArray(reservationData.tables) && reservationData.tables.length > 0) {
            await Promise.all(
              reservationData.tables.map(tableId => 
                updateDoc(doc(db, 'tables', tableId), {
                  reservedUntil: null
                })
              )
            );
          } else if (reservationData.tableId) {
            await updateDoc(doc(db, 'tables', reservationData.tableId), {
              reservedUntil: null
            });
          }
          break;
            
        case 'RESERVA CANCELADA':
          // Registrar cancelación y liberar mesas
          updateData.cancelledAt = new Date().toISOString();
          
          // Liberar mesas
          if (Array.isArray(reservationData.tables) && reservationData.tables.length > 0) {
            await Promise.all(
              reservationData.tables.map(tableId => 
                updateDoc(doc(db, 'tables', tableId), {
                  reservedUntil: null
                })
              )
            );
          } else if (reservationData.tableId) {
            await updateDoc(doc(db, 'tables', reservationData.tableId), {
              reservedUntil: null
            });
          }
          break;
            
        case 'NO SHOW':
          // Registrar no show y liberar mesas
          updateData.noShowAt = new Date().toISOString();
          
          // Liberar mesas
          if (Array.isArray(reservationData.tables) && reservationData.tables.length > 0) {
            await Promise.all(
              reservationData.tables.map(tableId => 
                updateDoc(doc(db, 'tables', tableId), {
                  reservedUntil: null
                })
              )
            );
          } else if (reservationData.tableId) {
            await updateDoc(doc(db, 'tables', reservationData.tableId), {
              reservedUntil: null
            });
          }
          break;
      }
      
      // Actualizamos la reserva con los nuevos datos
      await updateDoc(reservationRef, updateData);
      
      return true;
    } catch (error) {
      console.error('Error updating reservation status:', error);
      throw error;
    }
  },

  // Obtener estadísticas del día
  getDailyStats: async (date) => {
    try {
      const reservationsRef = collection(db, 'reservations');
      const q = query(
        reservationsRef,
        where('date', '==', date)
      );
      
      const snapshot = await getDocs(q);
      const reservations = snapshot.docs.map(doc => doc.data());

      return {
        totalReservations: reservations.length,
        confirmedReservations: reservations.filter(r => r.status === 'RESERVA CONFIRMADA').length,
        pendingReservations: reservations.filter(r => r.status === 'PENDIENTE DE CONFIRMACION').length,
        cancelledReservations: reservations.filter(r => r.status === 'RESERVA CANCELADA').length,
        totalGuests: reservations
          .filter(r => r.status !== 'RESERVA CANCELADA')
          .reduce((sum, r) => sum + parseInt(r.guests || 0), 0),
        byZone: {
          salon: reservations.filter(r => r.zone === 'Salón').length,
          terraza: reservations.filter(r => r.zone === 'Terraza').length,
          privado: reservations.filter(r => r.zone === 'Privado').length
        }
      };
    } catch (error) {
      console.error('Error getting daily stats:', error);
      throw error;
    }
  },

  // Gestión de mesas
  tables: {
    getAll: async () => {
      try {
        const tablesRef = collection(db, 'tables');
        const snapshot = await getDocs(tablesRef);
        
        const currentTime = new Date();
        const formattedCurrentDate = currentTime.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        
        // Obtener las reservas actuales para la fecha actual
        const reservationsRef = collection(db, 'reservations');
        const q = query(
          reservationsRef,
          where('date', '==', formattedCurrentDate),
          where('status', 'in', ['RESERVA CONFIRMADA', 'PENDIENTE DE CONFIRMACION', 'SENTADA', 'LLEGADA'])
        );
        const reservationsSnapshot = await getDocs(q);
        const currentReservations = reservationsSnapshot.docs.map(doc => doc.data());
    
        // Extraer todas las mesas
        const allTables = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          name: doc.data().name || doc.id
        }));
        
        // Verificar el estado actual de cada mesa
        return await Promise.all(allTables.map(async (table) => {
          // Verificar si la mesa está ocupada en este momento
          const currentHour = `${String(currentTime.getHours()).padStart(2, '0')}:${String(currentTime.getMinutes()).padStart(2, '0')}`;
          
          // Verificar si hay alguna reserva que incluya esta mesa y esté activa ahora
          const isTableOccupiedNow = currentReservations.some(reservation => {
            if (reservation.tables && reservation.tables.includes(table.id)) {
              const reservationTime = reservation.time;
              const reservationEndTime = minutesToTime(timeToMinutes(reservationTime) + RESERVATION_DURATION);
              
              return checkTimeOverlap(
                currentHour,
                currentHour, // Solo verificamos el momento actual
                reservationTime,
                reservationEndTime
              );
            }
            return false;
          });
          
          // Verificar disponibilidad para todo el día (útil para mostrar estado en UI)
          const tableAvailabilityByHour = {};
          const hours = [];
          
          // Generar horas desde las 13:00 hasta las 23:00 en intervalos de 30 minutos
          for (let hour = 13; hour <= 23; hour++) {
            for (let minute of [0, 30]) {
              if (hour === 23 && minute === 30) break; // No incluir 23:30
              
              const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
              hours.push(timeString);
              
              // Verificar disponibilidad para esta hora
              const isAvailable = await adminService.checkTableAvailability(table.id, formattedCurrentDate, timeString);
              tableAvailabilityByHour[timeString] = isAvailable;
            }
          }
    
          return {
            ...table,
            status: isTableOccupiedNow ? 'occupied' : 'available',
            availabilityByHour: tableAvailabilityByHour
          };
        }));
      } catch (error) {
        console.error('Error getting tables:', error);
        throw error;
      }
    },
  
    create: async (tableData) => {
      try {
        // Verificar si ya existe una mesa con ese nombre
        const tablesRef = collection(db, 'tables');
        const q = query(tablesRef, where('name', '==', tableData.name));
        const existingTables = await getDocs(q);
        
        if (!existingTables.empty) {
          throw new Error('Ya existe una mesa con este nombre');
        }
  
        const tableToCreate = {
          name: tableData.name,
          capacity: parseInt(tableData.capacity),
          type: tableData.type,
          reservedUntil: null
        };
  
        const docRef = await addDoc(collection(db, 'tables'), tableToCreate);
        
        return {
          id: docRef.id,
          ...tableToCreate,
          status: 'available'
        };
      } catch (error) {
        console.error('Error creating table:', error);
        throw error;
      }
    },
  
    update: async (tableId, updates) => {
      try {
        // Verificar si el nuevo nombre ya existe en otra mesa
        if (updates.name) {
          const tablesRef = collection(db, 'tables');
          const q = query(
            tablesRef, 
            where('name', '==', updates.name)
          );
          const existingTables = await getDocs(q);
          
          if (!existingTables.empty) {
            const existingTable = existingTables.docs[0];
            if (existingTable.id !== tableId) {
              throw new Error('Ya existe una mesa con este nombre');
            }
          }
        }
  
        const tableRef = doc(db, 'tables', tableId);
        
        const validUpdates = {
          name: updates.name,
          capacity: updates.capacity,
          type: updates.type
        };
  
        // Eliminar campos undefined o null
        Object.keys(validUpdates).forEach(key => {
          if (validUpdates[key] === undefined || validUpdates[key] === null) {
            delete validUpdates[key];
          }
        });
  
        await updateDoc(tableRef, validUpdates);
        
        return {
          id: tableId,
          ...validUpdates,
          status: 'available'
        };
      } catch (error) {
        console.error('Error updating table:', error);
        throw error;
      }
    },

    deleteReservation: async (reservationId) => {
      try {
        const reservationRef = doc(db, 'reservations', reservationId);
        
        // Obtener la reserva actual para liberar sus mesas
        const reservationSnapshot = await getDoc(reservationRef);
        if (reservationSnapshot.exists()) {
          const reservationData = reservationSnapshot.data();
          
          // Liberar las mesas asociadas
          if (Array.isArray(reservationData.tables) && reservationData.tables.length > 0) {
            await Promise.all(
              reservationData.tables.map(tableId => 
                updateDoc(doc(db, 'tables', tableId), {
                  reservedUntil: null
                })
              )
            );
          }
        }
        
        // Eliminar la reserva
        await deleteDoc(reservationRef);
        
        return true;
      } catch (error) {
        console.error('Error deleting reservation:', error);
        throw error;
      }
    },
  
    delete: async (tableId) => {
      try {
        const tableRef = doc(db, 'tables', tableId);
        await deleteDoc(tableRef);
        return true;
      } catch (error) {
        console.error('Error deleting table:', error);
        throw error;
      }
    }
  }
};

export default adminService;